import React from 'react'

import _ from 'lodash'

import {Alert, P, H5} from '../UI/index.js'
import CarrierSelector from '../parcels/CarrierSelector.js'
import {formSpacing, CurrencyInput, FormRow, FormStack, TimePicker, Option, Input, Select, Toggle, TextArea} from '../UI/forms/index.js'
import {Carrier, Customer, Reseller} from 'types/types.js'
import t from '../../helpers/Languages.js'

interface ParcelSettingsProps {
    reseller: Reseller
    customer: Customer
    // The main customer that invoices this customer, its administration settings are used
    invoiceMainCustomer?: Customer
    carriers: Carrier[]
    onChange: (customer: Customer) => void
}

interface ParcelSettingsState {
    customer: Customer
}

export default class ParcelSettings extends React.Component<ParcelSettingsProps, ParcelSettingsState> {
    onChangeCarrier(event) {
        const customer = {...this.props.customer}

        const index = customer.settings.parcels.disabledCarriers.indexOf(event.target.value)

        if (index > -1) {
            customer.settings.parcels.disabledCarriers.splice(index, 1)
        } else {
            customer.settings.parcels.disabledCarriers.push(event.target.value)
        }

        this.props.onChange(customer)
    }

    onChangeValue(path, event) {
        const customer = {...this.props.customer}

        _.set(customer, path, event.target.value)

        this.props.onChange(customer)
    }

    onChangeValues(path, event) {
        const customer = {...this.props.customer}

        _.set(customer, path, event.target.values)

        this.props.onChange(customer)
    }

    onChangeChecked(path, event) {
        const customer = {...this.props.customer}
        _.set(customer, path, event.target.checked)
        this.props.onChange(customer)
    }

    render() {
        const {reseller, customer, carriers, invoiceMainCustomer} = this.props
        const invoiceCustomer = invoiceMainCustomer || customer

        const enabledCarriers = carriers.filter((carrier) => {
            return reseller.settings.parcels.carriers[carrier.name] && reseller.settings.parcels.carriers[carrier.name].enabled
        })

        const customerCarriers = _.map(_.filter(enabledCarriers, (carrier) => {
            return (customer.settings.parcels.disabledCarriers || []).indexOf(carrier.name) === -1
        }), 'name')

        const isSameDayActive = (customerCarriers.map((carrier) => reseller.settings.parcels.products['Same Day']?.[carrier])).includes(true)

        const priceTables = _.uniq(['Standaard', ..._.keys(reseller.settings.parcels.prices).sort()])
        const priceTablesNetherlands = priceTables.filter((priceTable) => priceTable !== 'Standaard')
        const priceTableNetherlands = invoiceCustomer.settings.parcels.priceTable === 'Standaard' ? '' : invoiceCustomer.settings.parcels.priceTable

        const enabledOptionsFilteredSorted = (_.keys(reseller.settings.parcels.products) || []).filter((product) => {
            return _.some(_.keys(reseller.settings.parcels.products[product]), (carrier) => {
                return reseller.settings.parcels.products[product][carrier]
            })
        }).sort()

        const enabledOptions = enabledOptionsFilteredSorted.map((option) => {
            return {value: option, title: option}
        })

        const dayToLabel = {
            monday: 'M',
            tuesday: 'D',
            wednesday: 'W',
            thursday: 'D',
            friday: 'V',
            saturday: 'Z',
            sunday: 'Z'
        }

        return (
            <FormStack style={{marginBottom: formSpacing.rowGap}}>
                {reseller.isShop &&
                    <Toggle
                        label='Stuur emailadressen niet naar externe vervoerders'
                        info='Email adressen worden niet naar externe vervoerders gestuurd. De ontvanger zal dus geen emails van de vervoerder ontvangen.'
                        checked={!!customer.settings.parcels.dontSendEmailToCarriers}
                        onChange={this.onChangeChecked.bind(this, 'settings.parcels.dontSendEmailToCarriers')}
                    />
                }

                {!reseller.isShop &&
                    <>
                        <H5 style={{marginBottom: 12}}>Vervoerders</H5>
                        <div style={{width: '100%', overflowX: 'auto'}}>
                            <CarrierSelector
                                label='Geactiveerde vervoerders'
                                size={40}
                                carriers={enabledCarriers}
                                values={customerCarriers}
                                onChange={this.onChangeCarrier.bind(this)}
                            />
                        </div>

                        <Toggle
                            label='Vrije vervoerders keuze'
                            checked={customer.settings.parcels.selectCarrier}
                            onChange={this.onChangeChecked.bind(this, 'settings.parcels.selectCarrier')}
                        />

                        {reseller.allowCustomerContracts &&
                            <>
                                <Toggle
                                    label='Eigen PostNL contract via klantportaal'
                                    checked={!!customer.settings.parcels.customPostNLContract}
                                    onChange={this.onChangeChecked.bind(this, 'settings.parcels.customPostNLContract')}
                                />

                                <Toggle
                                    label='Eigen DHL contract via klantportaal'
                                    checked={!!customer.settings.parcels.customDHLContract}
                                    onChange={this.onChangeChecked.bind(this, 'settings.parcels.customDHLContract')}
                                />
                            </>
                        }

                    </>
                }

                <Select
                    label='Verzendregels'
                    allowEmpty
                    value={customer.settings.parcels.shippingRulesId}
                    options={reseller.settings.parcels.shippingRules.map((rule) => {
                        return {value: rule.id, title: rule.name}
                    })}
                    onChange={this.onChangeValue.bind(this, 'settings.parcels.shippingRulesId')}
                />

                {enabledCarriers.map(({name}) => name).indexOf('Cycloon') > -1 && (customer.settings.parcels.disabledCarriers || []).indexOf('Cycloon') === -1 &&
                    <FormRow>
                        <Select
                            label='Lagere voorkeur voor Cycloon op vrijdag'
                            info='Cycloon krijgt een lagere voorkeur dan andere vervoerders voor pakketten die worden aangemeld op vrijdag, dit voorkomt vertraging van een pakket omdat Cycloon op zaterdag niet bezorgd.'
                            value={customer.settings.parcels.cycloonNoParcelsOnFriday}
                            onChange={this.onChangeValue.bind(this, 'settings.parcels.cycloonNoParcelsOnFriday')}
                        >
                            <option value=''>{`Gebruik standaard instelling (${reseller.settings.parcels.carriers.Cycloon.noParcelsOnFriday ? 'Ja' : 'Nee'})`}</option>
                            <option value='yes'>Ja</option>
                            <option value='no'>Nee</option>
                        </Select>

                        <div style={{flex: 1}} />
                    </FormRow>
                }

                {(enabledCarriers.map(({name}) => name).indexOf('PostNL-MSG') > -1 || _.map(enabledCarriers, 'name').indexOf('DHL-MSG') > -1) && ((customer.settings.parcels.disabledCarriers || []).indexOf('PostNL-MSG') === -1 || (customer.settings.parcels.disabledCarriers || []).indexOf('DHL-MSG') === -1) &&
                    <Input
                        label='Klantnaam bij Monta'
                        value={customer.settings.parcels.montaOriginName}
                        onChange={this.onChangeValue.bind(this, 'settings.parcels.montaOriginName')}
                    />
                }

                {_.map(enabledCarriers, 'name').indexOf('DPD-onlineparcel') > -1 && (customer.settings.parcels.disabledCarriers || []).indexOf('DPD-onlineparcel') === -1 &&
                    <Select
                        label='DPD - Onlineparcel zaterdagbezorging'
                        info='Zendingen die op vrijdag worden aangemeld worden op zaterdag bezorgd.'
                        value={customer.settings.parcels.dpdOnlineparcelSaturdayDelivery}
                        onChange={this.onChangeValue.bind(this, 'settings.parcels.dpdOnlineparcelSaturdayDelivery')}
                    >
                        <option value=''>{`Gebruik standaard instelling (${reseller.settings.parcels.carriers['DPD-onlineparcel'].saturdayDelivery ? 'Ja' : 'Nee'})`}</option>
                        <option value='yes'>Ja</option>
                        <option value='no'>Nee</option>
                    </Select>
                }

                {_.map(enabledCarriers, 'name').indexOf('GLS') > -1 && (customer.settings.parcels.disabledCarriers || []).indexOf('GLS') === -1 &&
                    <Select
                        label='GLS zaterdagbezorging'
                        info='Zendingen die op vrijdag worden aangemeld worden op zaterdag bezorgd.'
                        value={customer.settings.parcels.glsSaturdayDelivery}
                        onChange={this.onChangeValue.bind(this, 'settings.parcels.glsSaturdayDelivery')}
                    >
                        <option value=''>{`Gebruik standaard instelling (${reseller.settings.parcels.carriers.GLS.glsSaturdayDelivery ? 'Ja' : 'Nee'})`}</option>
                        <option value='yes'>Ja</option>
                        <option value='no'>Nee</option>
                    </Select>
                }

                {_.map(enabledCarriers, 'name').indexOf('DHL') > -1 && (customer.settings.parcels.disabledCarriers || []).indexOf('DHL') === -1 &&
                    <Select
                        label='Bedrijfsadressen via DHL Europlus'
                        info='Zendingen naar bedrijfsadressen worden verstuurd via het DHL Europlus netwerk.'
                        value={customer.settings.parcels.dhlEuroplusForBusinesAddress}
                        onChange={this.onChangeValue.bind(this, 'settings.parcels.dhlEuroplusForBusinesAddress')}
                    >
                        <option value=''>{`Gebruik standaard instelling (${reseller.settings.parcels.carriers.DHL.europlusForBusinesAddress ? 'Ja' : 'Nee'})`}</option>
                        <option value='yes'>Ja</option>
                        <option value='no'>Nee</option>
                    </Select>
                }

                {_.map(enabledCarriers, 'name').indexOf('DHL-QLS') > -1 && (customer.settings.parcels.disabledCarriers || []).indexOf('DHL-QLS') === -1 &&
                    <Select
                        label='Bedrijfsadressen via DHL Europlus'
                        info='Zendingen naar bedrijfsadressen worden verstuurd via het DHL Europlus netwerk.'
                        value={customer.settings.parcels.dhlQLSEuroplusForBusinesAddress}
                        onChange={this.onChangeValue.bind(this, 'settings.parcels.dhlQLSEuroplusForBusinesAddress')}
                    >
                        <option value=''>{`Gebruik standaard instelling (${reseller.settings.parcels.carriers['DHL-QLS'].europlusForBusinesAddress ? 'Ja' : 'Nee'})`}</option>
                        <option value='yes'>Ja</option>
                        <option value='no'>Nee</option>
                    </Select>
                }

                {['DHL', 'DHL-MSG', 'Cycloon'].map((carrier) => {
                    if (enabledCarriers.findIndex((c) => c.name === carrier) === -1) {
                        return
                    }

                    return (
                        <Select
                            key={carrier}
                            label={`Lagere voorkeur voor ${carrier} voor bedrijfsadressen`}
                            value={customer.settings.parcels[`${carrier}noParcelsForBusinessAddress`]}
                            onChange={this.onChangeValue.bind(this, `settings.parcels.${carrier}noParcelsForBusinessAddress`)}
                        >
                            <option value=''>{`Gebruik standaard instelling (${reseller.settings.parcels.carriers[carrier].noParcelsForBusinessAddress ? 'Ja' : 'Nee'})`}</option>
                            <option value='yes'>Ja</option>
                            <option value='no'>Nee</option>
                        </Select>
                    )
                })}

                <H5 style={{marginTop: 36, marginBottom: 12}}>Cutoff tijd en ophaalrit</H5>

                <FormRow>
                    {isSameDayActive &&
                        <TimePicker
                            label='Same Day cutoff'
                            info='Deze tijd bepaald tot hoelaat deze klant Same Day zendingen kan aanmelden. Zet deze op 00:00 om Same Day voor deze klant uit te schakelen.'
                            value={customer.settings.parcels.sameDayCuttoff}
                            onChange={this.onChangeValue.bind(this, 'settings.parcels.sameDayCuttoff')}
                        />
                    }
                    <TimePicker
                        label='Next Day cutoff'
                        info='Deze tijd bepaald tot hoelaat deze klant Next Day zendingen kan aanmelden.'
                        value={customer.settings.parcels.nextDayCuttoff}
                        onChange={this.onChangeValue.bind(this, 'settings.parcels.nextDayCuttoff')}
                    />

                    {!isSameDayActive &&
                        <div style={{flex: 1}} />
                    }
                </FormRow>

                <Toggle
                    label='Automatisch Same Day / Next Day'
                    info='Een zending wordt automatisch Same Day verzonden als deze voor de Same Day cutoff of na de Next Day cutoff wordt aangemeld'
                    checked={!!customer.settings.parcels.autoSameDayNextDay}
                    onChange={this.onChangeChecked.bind(this, 'settings.parcels.autoSameDayNextDay')}
                />

                {!reseller.isShop &&
                    <Toggle
                        label='Automatische ophaalrit'
                        info='Er wordt automatisch een ophaalrit ingepland om de zendingen voor deze klant binnen het ingestelde tijdvak op te halen.'
                        checked={!!customer.settings.parcels.autoPickupOrder}
                        onChange={this.onChangeChecked.bind(this, 'settings.parcels.autoPickupOrder')}
                    />
                }

                {customer.settings.parcels.autoPickupOrder &&
                    <>
                        <Toggle
                            label='Zendingen scannen bij ophalen'
                            info='Barcodes worden toegevoegd aan de ophaalrit en kunnen gescand worden.'
                            checked={!!customer.settings.parcels.scanParcelsAtPickup}
                            onChange={this.onChangeChecked.bind(this, 'settings.parcels.scanParcelsAtPickup')}
                        />

                        <FormRow>
                            <TextArea
                                label='Instructies ophaalrit'
                                value={customer.settings.parcels.autoPickupInstructions}
                                onChange={this.onChangeValue.bind(this, 'settings.parcels.autoPickupInstructions')}
                            />

                            <FormStack style={{flex: 1}}>
                                <CurrencyInput
                                    style={{width: 'calc(50% - 12px)'}}
                                    label='Prijs ophaalrit'
                                    value={customer.settings.parcels.autoPickupPrice}
                                    onChange={this.onChangeValue.bind(this, 'settings.parcels.autoPickupPrice')}
                                />
                            </FormStack>
                        </FormRow>

                        <Toggle
                            label='Retourrit plannen op ophaaldag'
                            info='Voor een retourrit wordt gekeken naar de ophaaldagen in plaats van de openingsdagen.'
                            checked={customer.settings.usePickupDaysForRetourOrder}
                            onChange={this.onChangeChecked.bind(this, 'settings.usePickupDaysForRetourOrder')}
                        />

                        <FormRow style={{alignItems: 'center'}}>
                            <P style={{flex: 1}}>Ophaaldagen</P>

                            <div style={{display: 'flex', flex: 1}}>
                                {_.keys(customer.settings.parcels.pickupDays).map((day) => {
                                    return (
                                        <Option
                                            key={day}
                                            label={dayToLabel[day]}
                                            checked={customer.settings.parcels.pickupDays[day]}
                                            disabled={!reseller.settings.account.openingHours[day].open || !customer.settings.openingHours[day].open}
                                            onChange={this.onChangeChecked.bind(this, `settings.parcels.pickupDays.${day}`)}
                                        />
                                    )
                                })}
                            </div>
                        </FormRow>

                        {isSameDayActive && customer.settings.parcels.sameDayCuttoff !== '00:00' &&
                            <FormRow style={{alignItems: 'center'}}>
                                <P style={{flex: 1}}>Ophaaltijdvak Same Day</P>

                                <FormRow style={{flex: 1}}>
                                    <TimePicker
                                        label='Starttijd'
                                        value={customer.settings.parcels.sameDayPickupStart}
                                        onChange={this.onChangeValue.bind(this, 'settings.parcels.sameDayPickupStart')}
                                    />

                                    <TimePicker
                                        label='Eindtijd'
                                        value={customer.settings.parcels.sameDayPickupEnd}
                                        onChange={this.onChangeValue.bind(this, 'settings.parcels.sameDayPickupEnd')}
                                    />
                                </FormRow>
                            </FormRow>
                        }

                        <FormRow style={{alignItems: 'center'}}>
                            <P style={{flex: 1}}>Ophaaltijdvak Next Day</P>

                            <FormRow style={{flex: 1}}>
                                <TimePicker
                                    label='Starttijd'
                                    value={customer.settings.parcels.nextDayPickupStart}
                                    onChange={this.onChangeValue.bind(this, 'settings.parcels.nextDayPickupStart')}
                                />

                                <TimePicker
                                    label='Eindtijd'
                                    value={customer.settings.parcels.nextDayPickupEnd}
                                    onChange={this.onChangeValue.bind(this, 'settings.parcels.nextDayPickupEnd')}
                                />
                            </FormRow>
                        </FormRow>

                    </>
                }

                <H5 style={{marginTop: 36, marginBottom: 12}}>Administratie</H5>

                {invoiceMainCustomer &&
                    <Alert variant='info' style={{marginBottom: 12}}>{`${t('invoicedViaMainCustomer')} ${invoiceMainCustomer.name}.`}</Alert>
                }

                {!reseller.isShop &&
                    <FormRow>
                        <Select
                            label='Tarieftabel Nederland'
                            placeholder='Standaard'
                            value={priceTableNetherlands}
                            disabled={!!invoiceMainCustomer}
                            options={priceTablesNetherlands.map((priceTable) => {
                                return {value: priceTable, title: priceTable}
                            })}
                            onChange={this.onChangeValue.bind(this, 'settings.parcels.priceTable')}
                        />

                        <Select
                            label='Tarieftabel buitenland'
                            placeholder='Gebruik tarieftabel Nederland'
                            options={priceTables.map((priceTable) => {
                                return {value: priceTable, title: priceTable}
                            })}
                            value={invoiceCustomer.settings.parcels.priceTableAbroad}
                            disabled={!!invoiceMainCustomer}
                            onChange={this.onChangeValue.bind(this, 'settings.parcels.priceTableAbroad')}
                        />
                    </FormRow>
                }

                <FormRow>
                    <Select
                        label='Toon prijsopbouw op facturen'
                        value={invoiceCustomer.settings.parcels.showPriceExplanation}
                        onChange={this.onChangeValue.bind(this, 'settings.parcels.showPriceExplanation')}
                        options={[
                            {
                                title: 'Gebruik standaard instelling',
                                value: 'default'
                            },
                            {
                                title: 'Ja',
                                value: 'yes'
                            },
                            {
                                title: 'Nee',
                                value: ''
                            }
                        ]}
                        disabled={!!invoiceMainCustomer || (!invoiceCustomer.settings.parcels.priceTable && !invoiceCustomer.settings.parcels.priceTableAbroad)}
                    />

                    <div style={{flex: 1}} />
                </FormRow>

                <Toggle
                    label='Referentie verplicht'
                    info='Bij het aanmaken van een zending is het verplicht een referentie in te vullen.'
                    checked={!!customer.settings.parcels.referenceRequiredReseller}
                    onChange={this.onChangeChecked.bind(this, 'settings.parcels.referenceRequiredReseller')}
                />

                <H5 style={{marginTop: 36, marginBottom: 12}}>Overig</H5>

                <FormRow>
                    <Input
                        label={t('vatNumber')}
                        info='Bij sommige vervoerders moet een geldig BTW nummer en/of EORI nummer meegestuurd worden bij zendingen naar landen waar douane informatie vereist is.'
                        value={customer.settings.parcels.vatNumber}
                        onChange={this.onChangeValue.bind(this, 'settings.parcels.vatNumber')}
                    />

                    <Input
                        label={t('eoriNumber')}
                        info='Bij sommige vervoerders moet een geldig BTW nummer en/of EORI nummer meegestuurd worden bij zendingen naar landen waar douane informatie vereist is.'
                        value={customer.settings.parcels.eoriNumber}
                        onChange={this.onChangeValue.bind(this, 'settings.parcels.eoriNumber')}
                    />
                </FormRow>

                <Select
                    noSort
                    label='Standaard verzendopties'
                    info='Selecteer de opties die standaard gebruikt worden bij het aanmelden van een pakket. De geselecteerde opties zijn niet voor elke zending beschikbaar, als een combinatie van opties niet mogelijk is wordt de voorkeur gegeven aan de eerst geselecteerde optie.'
                    values={customer.settings.parcels.defaultOptions || []}
                    onChange={this.onChangeValues.bind(this, 'settings.parcels.defaultOptions')}
                    options={enabledOptions}
                />

                {(reseller.name === 'Tour de Ville Eindhoven' || reseller.name === 'Tour de Ville Tilburg' || reseller.name === 'Veloyd') &&
                    <Toggle
                        label='Verzendoptie Px beschikbaar'
                        checked={!!customer.settings.pxEnabled}
                        onChange={this.onChangeChecked.bind(this, 'settings.pxEnabled')}
                    />
                }

                {customer.settings.parcels.defaultOptions?.includes('Verzekerd') && (
                    <>
                        <CurrencyInput
                            label='Bedrag verzekerd'
                            value={customer.settings.parcels?.coverAmount || '0,00'}
                            onChange={this.onChangeValue.bind(this, 'settings.parcels.coverAmount')}
                        />
                    </>
                )}

                {!reseller.isShop &&
                    <>
                        <FormRow>
                            <Select
                                label='Retouren via'
                                options={[
                                    {value: '', title: 'Gebruik standaard instelling'},
                                    {value: 'reseller', title: reseller.settings?.accountName || reseller.name},
                                    {value: 'customer', title: 'Klant'}
                                ]}
                                value={customer.settings.parcels.returnsVia}
                                onChange={this.onChangeValue.bind(this, 'settings.parcels.returnsVia')}
                            />

                            <Input
                                label='Emailadres zending notificaties'
                                value={customer.settings.parcels.emailTT}
                                onChange={this.onChangeValue.bind(this, 'settings.parcels.emailTT')}
                            />
                        </FormRow>
                    </>
                }
            </FormStack>

        )
    }
}
